import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';
import { z } from 'zod';

import { FeApi, useInjectFeApiContext } from '@/api/fe-api';
import { useMe } from '@/hooks/use-me';
import type { DeepUnknown } from '@/types/deep-unknown';

import { TaskSchema } from '../entities/task';

export namespace TasksQuery {
  export const key = (input: Input) => [
    'project-management',
    'tasks',
    input.siteId,
  ];

  export type Input = { siteId: string };
  export const OutputSchema = z.object({
    tasks: z.array(TaskSchema.pick({ id: true, title: true })),
  });
  export type Output = z.infer<typeof OutputSchema>;

  function outputMapper(data: FeApi.ListCardsViewOutput): DeepUnknown<Output> {
    return {
      tasks: data.existing_cards.map((card) => ({
        id: card.uuid,
        title: card.title,
      })),
    };
  }

  function useQueryFn() {
    const tasksList = useInjectFeApiContext(FeApi.tasksV1CardsListRetrieve);
    const siteDetails = useInjectFeApiContext(
      FeApi.customerInfosV2SitesDetailRetrieve,
    );
    const me = useMe();

    return async function queryFn(input: Input) {
      const site = await ok(siteDetails({ accureId: input.siteId }));

      const tasksResult = await ok(
        tasksList({
          customerType: me.config.type,
          cardType: 'Standard',
          includeWorkstreamsCards: false,
          frontendId: site.full_frontend_id,
        }),
      );

      const output = await OutputSchema.parseAsync(outputMapper(tasksResult));

      return output;
    };
  }

  export function useQuery(input: Input) {
    const queryFn = useQueryFn();

    return rq.useQuery<Output>({
      queryKey: key(input),
      queryFn: () => queryFn(input),
    });
  }

  export function usePrefetch() {
    const queryClient = rq.useQueryClient();
    const queryFn = useQueryFn();

    return async function prefetch(input: Input): Promise<void> {
      await queryClient.prefetchQuery<Output>({
        queryKey: key(input),
        queryFn: () => queryFn(input),
      });
    };
  }
}
